/*
  Galaxy scroll motion (Theme settings > Animations > Scroll motion).

  Subtle, editorial motion tied to the scroll position (it plays backwards when the shopper
  scrolls up), built on GSAP + ScrollTrigger (assets/gsap.min.js, assets/gsap-scrolltrigger.min.js):
  - Top banners (Horizon hero, collection banner): the photo drifts down slower than the page and
    fades into the brand cream; the title rises and fades out.
  - Product cards: rise 32px into place and come up from 35% opacity, the columns of a row a few
    pixels apart so a row settles in a soft wave. Only cards below the screen when the motion
    starts: those already in view never move.
  - Product page gallery (desktop): each photo drifts a few percent inside its frame.

  Speed: GSAP is fetched only once the page is idle, nothing animates but transform and opacity,
  and phones get the lighter set (no parallax, shorter rises). Reduced-motion visitors get none.
  On desktop Horizon scrolls inside .page-wrapper, on phones the window: both are handled.

  Loaded from snippets/galaxy-head.liquid, which passes the GSAP file URLs as data attributes.
*/
(function () {
  var script = document.currentScript;
  if (!script || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var sources = [script.dataset.gsap, script.dataset.scrolltrigger];
  var CARD_SELECTOR = 'product-card, .galaxy-card';

  function load(src) {
    return new Promise(function (resolve, reject) {
      var tag = document.createElement('script');
      tag.src = src;
      tag.async = false;
      tag.onload = resolve;
      tag.onerror = reject;
      document.head.appendChild(tag);
    });
  }

  function whenIdle(callback) {
    var run = function () {
      if ('requestIdleCallback' in window) requestIdleCallback(callback, { timeout: 1500 });
      else setTimeout(callback, 300);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  whenIdle(function () {
    Promise.all(sources.map(load)).then(init, function () {});
  });

  function init() {
    var gsap = window.gsap;
    var ScrollTrigger = window.ScrollTrigger;
    if (!gsap || !ScrollTrigger) return;
    gsap.registerPlugin(ScrollTrigger);

    var mm = gsap.matchMedia();
    mm.add(
      { desktop: '(min-width: 990px)', phone: '(max-width: 989px)', reduce: '(prefers-reduced-motion: reduce)' },
      function (context) {
        if (context.conditions.reduce) return;
        var desktop = context.conditions.desktop;
        var scroller = desktop ? document.querySelector('.page-wrapper') || window : window;
        ScrollTrigger.defaults({ scroller: scroller });

        heroes(desktop);
        cards(document, desktop);
        if (desktop) gallery();

        var observer = watchNewCards(desktop);
        return function () {
          observer.disconnect();
          document.querySelectorAll('.galaxy-motion-veil').forEach(function (veil) {
            veil.remove();
          });
          document.querySelectorAll('[data-galaxy-motion]').forEach(function (el) {
            el.removeAttribute('data-galaxy-motion');
          });
        };
      }
    );

    // Theme editor: sections re-render in place.
    document.addEventListener('shopify:section:load', function () {
      ScrollTrigger.refresh();
    });

    function heroes(desktop) {
      var banners = [];
      document.querySelectorAll('.hero').forEach(function (hero) {
        banners.push({
          root: hero,
          media: hero.querySelector('.hero__media-grid'),
          content: hero.querySelector('.hero__content-wrapper'),
        });
      });
      document.querySelectorAll('[class^="ai-collection-hero-background-"]').forEach(function (background) {
        banners.push({
          root: background.parentElement,
          media: background,
          content: background.parentElement.querySelector('[class^="ai-collection-hero-content-"]'),
        });
      });

      banners.forEach(function (banner) {
        if (!banner.media) return;
        var veil = document.createElement('div');
        veil.className = 'galaxy-motion-veil';
        veil.setAttribute('aria-hidden', 'true');
        banner.media.appendChild(veil);

        var timeline = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: { trigger: banner.root, start: 'top top', end: 'bottom top', scrub: 0.4 },
        });
        if (desktop) timeline.fromTo(banner.media, { yPercent: 0 }, { yPercent: 10 }, 0);
        timeline.fromTo(veil, { opacity: 0 }, { opacity: 0.85 }, 0);
        if (banner.content) {
          timeline.fromTo(
            banner.content,
            { y: 0, opacity: 1 },
            { y: desktop ? -36 : -18, opacity: 0.15, ease: 'power1.in' },
            0
          );
        }
      });
    }

    function cards(root, desktop) {
      var fresh = Array.prototype.filter.call(root.querySelectorAll(CARD_SELECTOR), function (card) {
        return !card.hasAttribute('data-galaxy-motion') && !card.closest('dialog');
      });
      if (!fresh.length) return;

      // Read every position first, then animate: mixing reads and writes would force a layout per card.
      // Cards already on screen stay put: GSAP starts a tween from its "from" state, so they would
      // drop to 35% and jump down, then slide back, a second after the page (or a filter) loaded.
      var viewport = window.innerHeight;
      var onScreen = fresh.map(function (card) {
        return card.getBoundingClientRect().top < viewport;
      });
      var columns = fresh.map(function (card) {
        return desktop ? columnIndex(card) : 0;
      });

      fresh.forEach(function (card, i) {
        card.setAttribute('data-galaxy-motion', '');
        if (onScreen[i]) return;
        var extra = columns[i] * 8;
        gsap.fromTo(
          card,
          { y: (desktop ? 32 : 16) + extra, opacity: 0.35 },
          {
            y: 0,
            opacity: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: card,
              start: 'top bottom',
              end: desktop ? 'top 72%' : 'top 85%',
              scrub: 0.5,
            },
          }
        );
      });
    }

    // Position of a card within its row (0, 1, 2, …), for the soft wave across a row.
    function columnIndex(card) {
      var parent = card.parentElement && card.parentElement.parentElement;
      if (!parent) return 0;
      var top = Math.round(card.getBoundingClientRect().top);
      var index = 0;
      parent.querySelectorAll(CARD_SELECTOR).forEach(function (other) {
        if (other === card) return;
        var rect = other.getBoundingClientRect();
        if (Math.round(rect.top) === top && rect.left < card.getBoundingClientRect().left) index += 1;
      });
      return Math.min(index, 4);
    }

    function gallery() {
      document.querySelectorAll('media-gallery .media-gallery__grid .product-media-container').forEach(function (frame) {
        var media = frame.querySelector('.product-media');
        if (!media || !frame.getClientRects().length) return;
        frame.classList.add('galaxy-motion-frame');
        gsap.fromTo(
          media,
          { yPercent: -3 },
          {
            yPercent: 3,
            ease: 'none',
            scrollTrigger: { trigger: frame, start: 'top bottom', end: 'bottom top', scrub: 0.5 },
          }
        );
      });
    }

    // Filters, sorting and "load more" swap the product grid in place: animate the new cards and
    // drop triggers whose element has left the page.
    function watchNewCards(desktop) {
      var main = document.getElementById('MainContent');
      var pending = 0;
      var observer = new MutationObserver(function (records) {
        var addsCards = records.some(function (record) {
          return Array.prototype.some.call(record.addedNodes, function (node) {
            return node.nodeType === 1 && (node.matches(CARD_SELECTOR) || node.querySelector(CARD_SELECTOR));
          });
        });
        if (!addsCards) return;
        clearTimeout(pending);
        pending = setTimeout(function () {
          ScrollTrigger.getAll().forEach(function (trigger) {
            if (trigger.trigger && !trigger.trigger.isConnected) trigger.kill();
          });
          cards(document, desktop);
          ScrollTrigger.refresh();
        }, 150);
      });
      if (main) observer.observe(main, { childList: true, subtree: true });
      return observer;
    }
  }
})();
